// COCKPIT.FINAL.6 — modèle d'état cockpit READ-ONLY commun (socle PC / tablette / mobile / WebXR).
// Fonction PURE : aucun hook, API, WS, timer, localStorage, ref React, videoRef, RTCPeerConnection,
// ni fonction de commande (ARM/TAKEOFF/LAND/RTL). CockpitState = LECTURE ; les écritures restent dans
// useRealOffboardControl / PilotInput. Le modèle COMPOSE les projections existantes (jamais recopiées).
import { deriveControlAuthority, deriveSafetyView, buildFlightPipeline, homePositionView } from "./pilotageOperator";
import { buildRealHud } from "./offboardControl";
import { deriveFlightSessionView } from "./flightSessions";

export function buildCockpitState(inputs) {
  const { droneId, drone, real, telemetryFresh, video, raspberryHealth, network, backendSession } = inputs || {};
  const r = real && typeof real === "object" ? real : {};
  const orch = r.orch || null;
  const status = r.status || null;
  const d = drone && typeof drone === "object" ? drone : null;
  const fresh = telemetryFresh === true;

  // --- projections réutilisées (pass-through strict, aucune logique dupliquée) ---
  const authority = deriveControlAuthority({ orch, status, telemetryFresh: fresh, conn: r.conn });
  const safety = deriveSafetyView({ drone: d, status, orch, telemetryFresh: fresh });
  const pipeline = buildFlightPipeline({ orchPhase: orch?.phase, pipeline: orch?.pipeline, reason: orch?.reason });
  const home = homePositionView(status);
  const telemetry = buildRealHud(status, r.safety, d);

  const sessionId = r.sessionId || null; // SID = EXACTEMENT real.sessionId

  const v = video && typeof video === "object" ? video : null;
  const rpiHealth = raspberryHealth && typeof raspberryHealth === "object" ? raspberryHealth : null;
  const bs = backendSession && typeof backendSession === "object" ? backendSession : null;

  const failsafe = safety.global === "FAILSAFE" || authority.authority === "FAILSAFE";
  const pilotRc = authority.authority === "PILOT_RC";
  const linkLost = r.phase === "link_lost";
  const criticalUnknown = r.criticalAction?.uncertain === true;
  const resultUnknown = Array.isArray(pipeline.steps) && pipeline.steps.some((s) => s.state === "result_unknown");
  const pipelineTerminal = pipeline.terminal || null;
  const alertList = [];
  if (failsafe) alertList.push("FAILSAFE");
  if (pilotRc) alertList.push("PILOT_RC");
  if (linkLost) alertList.push("LINK_LOST");
  if (criticalUnknown) alertList.push("CRITICAL_UNKNOWN");
  if (resultUnknown) alertList.push("RESULT_UNKNOWN");
  if (pipelineTerminal) alertList.push(`TERMINAL_${pipelineTerminal}`);

  return {
    version: 1,
    droneId: droneId || null,

    freshness: {
      telemetryFresh: fresh,
      lastSeen: d?.last_seen ?? null,
      telemetryState: d?.telemetry_state ?? null,
    },
    flightController: d?.flight_controller ?? null, // pass-through strict

    authority,
    safety,
    pipeline,

    flight: {
      phase: orch?.phase ?? r.phase ?? null,
      px4Mode: status?.px4_mode ?? null,
      offboardState: status?.state ?? status?.offboard_state ?? null,
      armed: r.safety?.armed ?? null,
      landedState: r.safety?.landed_state ?? null,
      movementAllowed: r.moveAllowed === true,
      realEnabled: r.realEnabled === true,
    },

    home,
    telemetry,

    control: {
      connection: r.conn ?? null,
      sessionId,
      gatewayForwarded: inputs?.gatewayForwarded ?? r.transport?.gateway_forwarded ?? null,
      canStartFlight: r.canStartFlight === true,
      criticalAction: r.criticalAction ?? null,
      speedProfile: r.speedProfile ?? null,
      profileLimits: r.profileLimits ?? null,
      input: inputs?.pilotInput ?? null, // COCKPIT.FINAL.7 — observabilité entrée pilote (READ-ONLY)
    },

    video: v ? {
      enabled: true,
      active: v.uiState ? v.uiState === "live" : null,
      uiState: v.uiState ?? null,
      playMode: v.playMode ?? null,
      metrics: v.metrics ?? null,
      videoStats: v.videoStats ?? null,
      diagnostic: v.diagnostic ?? null,
    } : { enabled: false, active: null, uiState: null, playMode: null, metrics: null, videoStats: null, diagnostic: null },

    network: network ?? null,

    raspberry: {
      overall: rpiHealth?.health?.overall ?? null,
      health: rpiHealth?.health ?? null,
      loading: rpiHealth?.loading ?? null,
      error: rpiHealth?.error ?? null,
    },

    flightSession: deriveFlightSessionView({
      sessionId,                                   // même SID que la lease
      liveFlightSession: status?.flight_session,
      backendSession: bs,
    }),

    alerts: {
      failsafe, pilotRc, linkLost, criticalUnknown, resultUnknown,
      pipelineTerminal, list: alertList,
    },
  };
}
